import DownhillSkiingIcon from '@mui/icons-material/DownhillSkiing'
import SnowboardingSharpIcon from '@mui/icons-material/SnowboardingSharp'
import { Avatar, Badge, Chip, Grid2 as Grid, Stack, Typography } from '@mui/material'
import md5 from 'md5'

export const getGravatarUrl = (email, size = 200) => {
  if (!email) return ''
  const hash = md5(email.toLowerCase().trim())
  return `https://www.gravatar.com/avatar/${hash}?d=identicon&s=${size}`
}

/**
 * Get the URL for a user's avatar, handling both Google OAuth and Gravatar images
 *
 * @param {Object} user - The user object
 * @param {string} [user.avatar] - The user's avatar URL from Google OAuth
 * @param {string} user.email - The user's email address for Gravatar fallback
 * @param {number} [size=200] - The desired size of the avatar in pixels
 * @returns {string} The URL to the user's avatar image
 */
export const avatarUrl = (user, size = 200) => {
  if (!user) {
    return ''
  }

  if (user?.avatar && !user.avatar.includes('googleusercontent.com')) {
    // Use the custom avatar URL if it's not from Google
    return user.avatar + '?d=identicon&s=' + size
  }

  return `https://www.gravatar.com/avatar/${user.resourceId}?d=identicon&s=${size}`
}

const avatarSizes = {
  mini: 24,
  small: 48,
  medium: 96,
  large: 180,
}

const nameSizes = {
  mini: 'h6',
  small: 'h5',
  medium: 'h4',
  large: 'h2',
}

const nameSpacing = {
  mini: 1,
  small: 2,
  medium: 2,
  large: 3,
}
/**
 * A reusable avatar component that displays a user's avatar image or fallback initial
 *
 * @param {Object} props - The component props
 * @param {Object} props.user - User object containing avatar/name info
 * @param {string} [props.size='small'] - Size variant ('small'|'medium'|'large')
 * @returns {JSX.Element} Avatar component with user image or initial
 *
 * @example
 * // Basic usage
 * <UserAvatar user={user} />
 *
 * // Large size variant
 * <UserAvatar user={user} size="large" />
 */
export const UserAvatar = ({ user, size = 'small' }) => {
  if (!user) {
    return ''
  }

  const name = user?.name?.charAt(0) || '?'

  return (
    <Badge color="primary" overlap="circular" anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} sx={{ '& .MuiBadge-badge': { backgroundColor: 'text.secondary' } }}>
      <Avatar
        src={avatarUrl(user, size === 'large' ? 512 : 96)}
        sx={{
          width: avatarSizes[size],
          height: avatarSizes[size],
          bgcolor: 'primary.main',
          border: size === 'large' ? '4px solid' : 'none',
          borderColor: 'background.paper',
        }}
      >
        {name}
      </Avatar>
    </Badge>
  )
}

// Maybe replace with UserCard or profile
export const AvatarPlus = ({ user, size = 'small' }) => {
  if (!user) {
    return null
  }
  return (
    <Grid container justifyContent="center" alignItems="center" spacing={nameSpacing[size]}>
      <Grid>
        <UserAvatar user={user} size={size} />
      </Grid>
      <Grid justifyContent="center" alignItems="center">
        <Typography
          variant={nameSizes[size]}
          gutterBottom
          sx={{
            mx: 0,
            mb: 0,
            color: 'text.primary',
            fontWeight: size === 'large' ? 'bold' : 'regular',
          }}
        >
          {user.name}
        </Typography>
        {user.sports && size !== 'mini' && (
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            {user.sports.map((sport) => (
              <Chip
                key={sport}
                icon={sport === 'SNOWBOARD' ? <SnowboardingSharpIcon /> : <DownhillSkiingIcon />}
                label={sport === 'SNOWBOARD' ? 'Snowboarder' : 'Skier'}
                variant="outlined"
                size={size === 'large' ? 'small' : 'small'}
                color={sport === 'SNOWBOARD' ? 'primary' : 'secondary'}
              />
            ))}
          </Stack>
        )}
      </Grid>
    </Grid>
  )
}
